import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  ExternalLink,
  BarChart3,
  MousePointer,
  ArrowLeft,
  Save,
  X
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CATEGORIES = ["Identity", "Growth", "Discipline", "Mastery", "Purpose", "Transformation"];
const PLACEMENTS = ["discover", "dashboard", "compass"];

export default function AdminQuests() {
  const navigate = useNavigate();
  const [quests, setQuests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingQuest, setEditingQuest] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Identity",
    thumbnail: "",
    cta_label: "Enter Quest",
    external_url: "",
    audience: "all",
    status: "active",
    sort_order: 0,
    placement: ["discover"]
  });

  const fetchQuests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quests`, {
        withCredentials: true
      });
      setQuests(Array.isArray(res.data?.quests) ? res.data.quests : []);
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error("Failed to load quests");
      }
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quest-analytics`, {
        withCredentials: true
      });
      setAnalytics(res.data);
    } catch (error) {
      // Analytics are supplementary - graceful degradation without error display
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch quest analytics:', error);
      }
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchQuests(), fetchAnalytics()]);
      setLoading(false);
    };
    loadData();
  }, [fetchQuests, fetchAnalytics]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingQuest) {
        await axios.put(
          `${API}/admin/quests/${editingQuest.quest_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success("Quest updated successfully");
      } else {
        await axios.post(
          `${API}/admin/quests`,
          formData,
          { withCredentials: true }
        );
        toast.success("Quest created successfully");
      }
      
      setShowEditor(false);
      setEditingQuest(null);
      resetForm();
      fetchQuests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save quest");
    }
  };

  const handleDelete = async (questId) => {
    if (!confirm("Are you sure you want to delete this quest?")) return;
    
    try {
      await axios.delete(`${API}/admin/quests/${questId}`, {
        withCredentials: true
      });
      toast.success("Quest deleted");
      fetchQuests();
    } catch (err) {
      toast.error("Failed to delete quest");
    }
  };

  const handleEdit = (quest) => {
    setEditingQuest(quest);
    setFormData({
      title: quest.title,
      description: quest.description,
      category: quest.category,
      thumbnail: quest.thumbnail || "",
      cta_label: quest.cta_label,
      external_url: quest.external_url,
      audience: quest.audience || "all",
      status: quest.status,
      sort_order: quest.sort_order,
      placement: quest.placement || ["discover"]
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "Identity",
      thumbnail: "",
      cta_label: "Enter Quest",
      external_url: "",
      audience: "all",
      status: "active",
      sort_order: 0,
      placement: ["discover"]
    });
  };

  const handleNewQuest = () => {
    setEditingQuest(null);
    resetForm();
    setShowEditor(true);
  };

  const togglePlacement = (place) => {
    setFormData(prev => ({
      ...prev,
      placement: prev.placement.includes(place)
        ? prev.placement.filter(p => p !== place)
        : [...prev.placement, place]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-white/50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#0B2821] to-background pt-6 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <button 
            onClick={() => navigate("/admin")}
            className="flex items-center hover:text-white mb-4 transition-colors"
            style={{ color: '#a69373' }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold" style={{ color: '#a69373' }}>Quest Management</h1>
              <p className="text-sm" style={{ color: 'rgba(166, 147, 115, 0.7)' }}>Manage external quest links to Google Drive</p>
            </div>
            <Button onClick={handleNewQuest} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Quest
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-4 space-y-6">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <MousePointer className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-white/60">Total Clicks</p>
                    <p className="text-2xl font-heading text-white">{analytics.total_clicks}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-white/60">Active Quests</p>
                    <p className="text-2xl font-heading text-white">
                      {quests.filter(q => q.status === "active").length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {analytics.top_quests?.[0] && (
              <Card className="glass-card col-span-2">
                <CardContent className="p-4">
                  <p className="text-xs mb-2 text-white/60">Top Quest</p>
                  <p className="font-heading text-white">{analytics.top_quests[0].title}</p>
                  <p className="text-sm text-white/70">{analytics.top_quests[0].clicks} clicks</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Quests List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle style={{ color: '#2d4a44' }}>All Quests</CardTitle>
          </CardHeader>
          <CardContent>
            {quests.length === 0 ? (
              <div className="text-center py-8">
                <p style={{ color: 'rgba(45, 74, 68, 0.6)' }}>No quests yet. Create your first quest!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {quests.map(quest => (
                  <div 
                    key={quest.quest_id}
                    className="flex items-center gap-4 p-4 bg-white/50 rounded-xl quest-card-light"
                  >
                    {quest.thumbnail ? (
                      <img 
                        src={quest.thumbnail} 
                        alt={quest.title}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ExternalLink className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold" style={{ color: '#1a3530' }}>{quest.title}</h3>
                        <Badge variant={quest.status === "active" ? "default" : "secondary"}>
                          {quest.status}
                        </Badge>
                        <Badge variant="outline" style={{ color: '#1a3530', borderColor: '#1a3530' }}>{quest.category}</Badge>
                      </div>
                      <p className="text-sm" style={{ color: '#2d4a44' }}>{quest.description}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs" style={{ color: 'rgba(26, 53, 48, 0.8)' }}>
                          {quest.click_count || 0} clicks
                        </span>
                        <span className="text-xs" style={{ color: 'rgba(26, 53, 48, 0.8)' }}>•</span>
                        <span className="text-xs" style={{ color: 'rgba(26, 53, 48, 0.8)' }}>
                          {quest.placement?.join(", ") || "discover"}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(quest.external_url, "_blank")}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(quest)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDelete(quest.quest_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-lg bg-background">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editingQuest ? "Edit Quest" : "Create New Quest"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-foreground/70 mb-1 block">Title</label>
              <Input
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                placeholder="Quest title"
                required
              />
            </div>
            
            <div>
              <label className="text-sm text-foreground/70 mb-1 block">Description</label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the quest"
                rows={3}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-foreground/70 mb-1 block">Category</label>
                <Select 
                  value={formData.category} 
                  onValueChange={v => setFormData({...formData, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm text-foreground/70 mb-1 block">Sort Order</label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-foreground/70 mb-1 block">External URL (Google Drive)</label>
              <Input
                value={formData.external_url}
                onChange={e => setFormData({...formData, external_url: e.target.value})}
                placeholder="https://drive.google.com/..."
                type="url"
                required
              />
            </div>
            
            <div>
              <label className="text-sm text-foreground/70 mb-1 block">Thumbnail URL (optional)</label>
              <Input
                value={formData.thumbnail}
                onChange={e => setFormData({...formData, thumbnail: e.target.value})}
                placeholder="https://..."
                type="url"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-foreground/70 mb-1 block">CTA Label</label>
                <Input
                  value={formData.cta_label}
                  onChange={e => setFormData({...formData, cta_label: e.target.value})}
                  placeholder="Enter Quest"
                />
              </div>
              
              <div>
                <label className="text-sm text-foreground/70 mb-1 block">Status</label>
                <Select 
                  value={formData.status} 
                  onValueChange={v => setFormData({...formData, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-foreground/70 mb-2 block">Show on</label>
              <div className="flex gap-2">
                {PLACEMENTS.map(place => (
                  <Badge
                    key={place}
                    variant={formData.placement.includes(place) ? "default" : "outline"}
                    className="cursor-pointer capitalize"
                    onClick={() => togglePlacement(place)}
                  >
                    {place}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                <Save className="w-4 h-4 mr-2" />
                {editingQuest ? "Update" : "Create"} Quest
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
